import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CreateRoleInput, Role, UpdateRoleInput } from '@/application/permissions/permissionsTypes';
import { createRole, deleteRole, listRoles, updateRole } from '@/infrastructure/http/permissionsApi';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { actionClass } from '@/presentation/components/common/actionButtons';
import { EmptyState } from '@/presentation/components/common/EmptyState';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { RoleRowActions } from '@/presentation/components/permissions/RoleRowActions';
import { RolesModal, type RolesModalMode } from '@/presentation/components/permissions/RolesModal';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { hasPermissionFromKeys, resolvePanelHomePath } from '@/application/navigation/menuHouses';
import { MAX_CUSTOM_ROLES } from '@/shared/permissions/roleTemplates';

export function PermissionsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { simulate, isOwner, isSimulating } = usePermissions();
  const canManage = isOwner && !isSimulating;

  const handleSimulate = useCallback(
    (role: Role) => {
      simulate({ name: role.name, permissionKeys: role.permissionKeys });
      navigate(resolvePanelHomePath(hasPermissionFromKeys(role.permissionKeys)));
    },
    [simulate, navigate]
  );

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<RolesModalMode>('create');
  const [modalRole, setModalRole] = useState<Role | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);

  const errorText = useCallback(
    (code: string) => t(`permissions.errors.${code}`, { defaultValue: t('permissions.errors.generic') }),
    [t]
  );

  const refresh = useCallback(async () => {
    if (!token || !canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setRoles(await listRoles(token));
    } catch (err) {
      setError(errorText(err instanceof Error ? err.message : 'generic'));
    } finally {
      setLoading(false);
    }
  }, [token, canManage, errorText]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const customRolesCount = roles.filter((r) => !r.isSystem).length;
  const atCustomRoleLimit = customRolesCount >= MAX_CUSTOM_ROLES;
  const limitTitle = atCustomRoleLimit ? t('permissions.limitReached', { max: MAX_CUSTOM_ROLES }) : undefined;

  const handleCreate = useCallback(
    async (input: CreateRoleInput) => {
      if (customRolesCount >= MAX_CUSTOM_ROLES) {
        throw new Error('panel_role_limit');
      }
      if (token) await createRole(token, input);
    },
    [token, customRolesCount]
  );

  const handleUpdate = useCallback(
    async (id: string, input: UpdateRoleInput) => {
      if (token) await updateRole(token, id, input);
    },
    [token]
  );

  if (!canManage) {
    return (
      <PanelPageState
        variant="forbidden"
        domain="team"
        layout="page"
        data-testid="team-roles-forbidden"
      />
    );
  }

  const openModal = (mode: RolesModalMode, role: Role | null) => {
    if ((mode === 'create' || mode === 'duplicate') && atCustomRoleLimit) {
      return;
    }
    setModalMode(mode);
    setModalRole(role);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete) return;
    const role = pendingDelete;
    setPendingDelete(null);
    setError(null);
    try {
      await deleteRole(token, role.id);
      await refresh();
    } catch (err) {
      setError(errorText(err instanceof Error ? err.message : 'generic'));
    }
  };

  const createButton = (
    <button
      onClick={() => openModal('create', null)}
      disabled={atCustomRoleLimit}
      title={limitTitle}
      className={`${actionClass('primary', 'page')} w-full sm:w-auto`}
    >
      {t('permissions.createRole')}
    </button>
  );

  const createToolbar = (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-3">
      {!loading && (
        <p className="text-xs tabular-nums text-gray-500 sm:text-right dark:text-gray-400">
          {t('permissions.customCount', { count: customRolesCount, max: MAX_CUSTOM_ROLES })}
        </p>
      )}
      {createButton}
    </div>
  );

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-4 p-4 xl:p-6'}>
      {embedded ? (
        createToolbar
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('permissions.title')}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('permissions.description')}</p>
          </div>
          {createToolbar}
        </div>
      )}

      {error && (
        <PanelPageState variant="error" layout="inline" onRetry={() => void refresh()} />
      )}

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {loading ? (
          <LoadingBlock minHeightClassName="min-h-[8rem]" />
        ) : roles.length === 0 ? (
          <EmptyState title={t('permissions.emptyTitle')} message={t('permissions.empty')} />
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-800">
            {roles.map((role) => (
              <li
                key={role.id}
                className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${!role.isActive ? 'opacity-70' : ''}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{role.name}</span>
                  {role.isSystem && (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                      {t('permissions.systemBadge')}
                    </span>
                  )}
                  {!role.isActive && <span className="text-xs text-amber-600 dark:text-amber-400">({t('permissions.inactive')})</span>}
                </div>
                <RoleRowActions
                  role={role}
                  atLimit={atCustomRoleLimit}
                  limitTitle={limitTitle}
                  onEdit={() => openModal('edit', role)}
                  onSimulate={() => handleSimulate(role)}
                  onDuplicate={() => openModal('duplicate', role)}
                  onDelete={() => setPendingDelete(role)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {modalOpen && (
        <RolesModal
          mode={modalMode}
          role={modalRole}
          rolesForCompare={roles}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onSaved={refresh}
          onClose={() => setModalOpen(false)}
        />
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 font-bold text-gray-900 dark:text-white">{t('permissions.deleteRole')}</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">{t('permissions.confirmDelete', { name: pendingDelete.name })}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingDelete(null)} className="rounded-lg px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{t('common.cancel')}</button>
              <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
